/**
 * Theme Generator
 *
 * Generates CSS variables from color preset and user preferences
 * Handles light/dark mode conversion automatically
 */

import { applyDensity, BASE_SPACING, type DensityLevel } from '../tokens/spacing';
import type { ColorPreset, ColorScale } from '../tokens/colors';
import { pickFg, shade } from './color-helpers';

// ============================================================================
// TYPES
// ============================================================================

export interface ThemeConfig {
  /** Color preset */
  colorPreset: ColorPreset;
  /** Light or dark mode */
  mode: 'light' | 'dark';
  /**
   * Body font family stack (the same value goes to both `--font-family`
   * (back-compat) and the new `--font-body`).
   */
  fontFamily: string;
  /**
   * Phase 4.4b-1 — display/heading font stack. Optional; when omitted
   * the display token mirrors the body font so designs that don't opt
   * into a separate display face look identical to before.
   */
  fontDisplay?: string;
  /** Density level */
  density: DensityLevel;
}

export interface ThemeVariables {
  /** Color CSS variables */
  colors: Record<string, string>;
  /** Spacing CSS variables */
  spacing: Record<string, string>;
  /** Typography CSS variables */
  typography: Record<string, string>;
}

// ============================================================================
// COLOR CONVERSION
// ============================================================================

/**
 * Convert hex to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Convert RGB to HSL
 */
const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Convert HSL to hex
 */
const hslToHex = (h: number, s: number, l: number): string => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Adjust color for dark mode
 * Inverts lightness while maintaining hue and saturation
 */
const adjustForDarkMode = (hex: string): string => {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Invert lightness (light becomes dark, dark becomes light)
  const invertedLightness = 100 - hsl.l;

  // Adjust saturation slightly for better visibility
  const adjustedSaturation = hsl.s * 0.9;

  return hslToHex(hsl.h, adjustedSaturation, invertedLightness);
};

// ============================================================================
// COLOR SCALE CONVERSION
// ============================================================================

/**
 * Convert color scale for dark mode
 */
const convertScaleForDarkMode = (scale: ColorScale): ColorScale => ({
  50: adjustForDarkMode(scale[900]), // Darkest becomes lightest
  100: adjustForDarkMode(scale[800]),
  200: adjustForDarkMode(scale[700]),
  300: adjustForDarkMode(scale[600]),
  400: adjustForDarkMode(scale[500]),
  500: adjustForDarkMode(scale[400]), // Middle stays middle-ish
  600: adjustForDarkMode(scale[300]),
  700: adjustForDarkMode(scale[200]),
  800: adjustForDarkMode(scale[100]),
  900: adjustForDarkMode(scale[50]), // Lightest becomes darkest
});

// ============================================================================
// CSS VARIABLE GENERATION
// ============================================================================

/**
 * Generate color CSS variables from color scale
 */
const generateColorVars = (
  prefix: string,
  scale: ColorScale,
  mode: 'light' | 'dark'
): Record<string, string> => {
  const vars: Record<string, string> = {};
  const finalScale = mode === 'dark' ? convertScaleForDarkMode(scale) : scale;

  Object.entries(finalScale).forEach(([shade, color]) => {
    vars[`--color-${prefix}-${shade}`] = color;
  });

  return vars;
};

/**
 * Generate all color CSS variables
 */
const generateAllColorVars = (
  preset: ColorPreset,
  mode: 'light' | 'dark'
): Record<string, string> => ({
  ...generateColorVars('primary', preset.primary, mode),
  ...generateColorVars('secondary', preset.secondary, mode),
  ...generateColorVars('tertiary', preset.tertiary, mode),
  ...generateColorVars('neutral', preset.neutral, mode),
  ...generateColorVars('success', preset.semantic.success, mode),
  ...generateColorVars('warning', preset.semantic.warning, mode),
  ...generateColorVars('error', preset.semantic.error, mode),
  ...generateColorVars('info', preset.semantic.info, mode),
  ...generateSemanticAliasVars(preset, mode),
});

// ============================================================================
// SEMANTIC ALIASES (Phase 4.4c-1)
// ============================================================================
//
// The approved auth + onboarding design references a set of semantic
// CSS variables (`--color-bg`, `--color-text`, `--color-line`,
// `--color-primary`, `--color-primary-fg`, `--color-primary-hover`,
// `--color-primary-soft`, …) rather than the existing
// `--color-{name}-{shade}` scale. We emit BOTH so callers can pick
// the level of indirection that matches their need; the existing scale
// vars stay for back-compat.
//
// Surface / line / text values are fixed-per-mode (the design uses an
// editorial palette that doesn't derive from the active preset).
// Primary aliases are preset-aware via `pickFg` / `shade`.

interface SemanticPalette {
  bg: string;
  surface: string;
  surface2: string;
  line: string;
  lineSoft: string;
  text: string;
  textSoft: string;
  textFaint: string;
}

const SEMANTIC_LIGHT: SemanticPalette = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surface2: '#F7F7F8',
  line: '#E5E5E7',
  lineSoft: '#EFEFF1',
  text: '#0E0E10',
  textSoft: '#5E5E66',
  textFaint: '#666670',
};

const SEMANTIC_DARK: SemanticPalette = {
  bg: '#0E0E10',
  surface: '#16161A',
  surface2: '#1C1C21',
  line: '#2A2A30',
  lineSoft: '#202024',
  text: '#F2F2F4',
  textSoft: '#A4A4AC',
  textFaint: '#A4A4AC',
};

const generateSemanticAliasVars = (
  preset: ColorPreset,
  mode: 'light' | 'dark',
): Record<string, string> => {
  const palette = mode === 'dark' ? SEMANTIC_DARK : SEMANTIC_LIGHT;
  // The active preset's primary[500] is the canonical brand color in
  // BOTH modes — the dark-mode scale-conversion only applies to the
  // numeric scale, not the singular `--color-primary` alias. Designs
  // expect "purple stays purple" when the user toggles dark mode.
  const primary = preset.primary[500];
  const primaryHover = mode === 'dark' ? shade(primary, 0.12) : shade(primary, -0.1);
  const primarySoft = mode === 'dark' ? shade(primary, -0.55) : shade(primary, 0.86);
  const primaryText = mode === 'dark' ? preset.primary[200] : preset.primary[900];
  // ChatGPT-style accents use the active brand color itself rather than a
  // deep 900-tier fill. A small darkening keeps the lightest approved accent
  // visibly separated from the surface while preserving the lighter feel.
  const primaryAction = mode === 'dark' ? preset.primary[200] : shade(preset.primary[500], -0.04);
  const primaryActionFg = pickFg(primaryAction);
  // Dark text needs a slightly lighter hover to preserve contrast; white text
  // can use the conventional darker hover treatment.
  const primaryActionHover =
    mode === 'dark'
      ? preset.primary[100]
      : primaryActionFg === '#FFFFFF'
        ? shade(primaryAction, -0.1)
        : shade(primaryAction, 0.02);
  const semanticText = {
    success: mode === 'dark' ? preset.semantic.success[200] : preset.semantic.success[800],
    warning:
      mode === 'dark'
        ? preset.semantic.warning[200]
        : shade(preset.semantic.warning[900], -0.4),
    danger: mode === 'dark' ? preset.semantic.error[200] : preset.semantic.error[800],
    info: mode === 'dark' ? preset.semantic.info[200] : preset.semantic.info[800],
  };
  const semanticSoft = {
    success: mode === 'dark' ? shade(preset.semantic.success[900], -0.6) : preset.semantic.success[50],
    warning: mode === 'dark' ? shade(preset.semantic.warning[900], -0.6) : preset.semantic.warning[50],
    danger: mode === 'dark' ? shade(preset.semantic.error[900], -0.6) : preset.semantic.error[50],
    info: mode === 'dark' ? shade(preset.semantic.info[900], -0.6) : preset.semantic.info[50],
  };
  return {
    '--color-bg': palette.bg,
    '--color-surface': palette.surface,
    '--color-surface-2': palette.surface2,
    '--color-line': palette.line,
    '--color-line-soft': palette.lineSoft,
    '--color-text': palette.text,
    '--color-text-soft': palette.textSoft,
    '--color-text-faint': palette.textFaint,
    '--color-primary': primary,
    '--color-primary-fg': pickFg(primary),
    '--color-primary-hover': primaryHover,
    '--color-primary-soft': primarySoft,

    '--a-bg': palette.bg,
    '--a-surface': palette.surface,
    '--a-surface-2': palette.surface2,
    '--a-line': palette.line,
    '--a-line-soft': palette.lineSoft,
    '--a-text': palette.text,
    '--a-text-soft': palette.textSoft,
    '--a-text-faint': palette.textFaint,
    '--a-primary': primary,
    '--a-primary-text': primaryText,
    '--a-primary-fg': pickFg(primary),
    '--a-primary-hover': primaryHover,
    '--a-primary-soft': primarySoft,
    '--a-primary-action': primaryAction,
    '--a-primary-action-fg': primaryActionFg,
    '--a-primary-action-hover': primaryActionHover,
    '--a-success': semanticText.success,
    '--a-success-soft': semanticSoft.success,
    '--a-warning': semanticText.warning,
    '--a-warning-soft': semanticSoft.warning,
    '--a-danger': semanticText.danger,
    '--a-danger-soft': semanticSoft.danger,
    '--a-info': semanticText.info,
    '--a-info-soft': semanticSoft.info,
    '--a-radius-sm': '6px',
    '--a-radius-md': '8px',
    '--a-radius-lg': '12px',
    // A transient notice needs a quieter layer than an interactive popover:
    // enough separation from the work beneath it, without reading as a modal.
    '--a-shadow-toast': mode === 'dark'
      ? '0 6px 18px rgba(0,0,0,0.32)'
      : '0 6px 18px rgba(0,0,0,0.14)',
    '--a-shadow-popover': '0 14px 36px rgba(0,0,0,0.18)',
    '--a-shadow-modal': '0 24px 64px rgba(0,0,0,0.22)',
    '--a-focus-ring': '0 0 0 3px rgba(17,17,17,0.08)',
  };
};

/**
 * Generate spacing CSS variables with density
 */
const generateSpacingVars = (density: DensityLevel): Record<string, string> => {
  const vars: Record<string, string> = {};

  Object.entries(BASE_SPACING).forEach(([key, value]) => {
    vars[`--spacing-${key}`] = `${applyDensity(value, density)}px`;
  });

  return vars;
};

/**
 * Generate typography CSS variables.
 *
 * Phase 4.4b-1 — adds `--font-body` alongside the existing
 * `--font-family` (kept as a back-compat alias) and a new
 * `--font-display` for editorial headings. When no separate display
 * font is configured, `--font-display` mirrors the body font so
 * existing themes look identical to before this change.
 */
const generateTypographyVars = (
  fontFamily: string,
  fontDisplay?: string,
): Record<string, string> => {
  const display = fontDisplay ?? fontFamily;

  return {
    '--font-family': fontFamily,
    '--font-body': fontFamily,
    '--font-display': display,
    '--a-body': fontFamily,
    '--a-display': display,
  };
};

// ============================================================================
// MAIN GENERATOR FUNCTION
// ============================================================================

/**
 * Generate complete theme variables
 */
export const generateThemeVariables = (config: ThemeConfig): ThemeVariables => ({
  colors: generateAllColorVars(config.colorPreset, config.mode),
  spacing: generateSpacingVars(config.density),
  typography: generateTypographyVars(config.fontFamily, config.fontDisplay),
});

/**
 * Apply theme variables to document
 */
export const applyThemeVariables = (
  variables: ThemeVariables,
  element: HTMLElement = document.documentElement
): void => {
  // Apply all variables
  Object.entries(variables.colors).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });

  Object.entries(variables.spacing).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });

  Object.entries(variables.typography).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
};

/**
 * Generate and apply theme
 */
export const applyTheme = (config: ThemeConfig, element?: HTMLElement): void => {
  const variables = generateThemeVariables(config);
  applyThemeVariables(variables, element);

  // Update data attribute for CSS selectors
  const root = element ?? document.documentElement;
  root.setAttribute('data-theme', config.mode);
  root.setAttribute('data-color-preset', config.colorPreset.id);
  root.setAttribute('data-density', config.density);
};

/**
 * Remove theme variables
 */
export const removeThemeVariables = (element: HTMLElement = document.documentElement): void => {
  // Remove all CSS variables emitted by this theme generator.
  const styles = element.style;
  for (let i = styles.length - 1; i >= 0; i--) {
    const property = styles[i];
    if (
      property.startsWith('--a-') ||
      property.startsWith('--color-') ||
      property.startsWith('--spacing-') ||
      property.startsWith('--font-')
    ) {
      element.style.removeProperty(property);
    }
  }

  // Remove data attributes
  element.removeAttribute('data-theme');
  element.removeAttribute('data-color-preset');
  element.removeAttribute('data-density');
};
